import { useState, useEffect } from "react";
import { AdminOrderService } from "../../../../../services/api/AdminOrderService/admin.order.service";
import { OfflineItemService } from "../../../../../services/api/OfflineOrder/offline.service";
import medicineIcon from "../../../../../assets/images/medicineIcon.png";
import PatientDetailsForm from "./PatientDetailsForm";
import ClinicOrderSummary from "./ClinicOrderSummary";
import toast from "react-hot-toast";
import OfflineBill from "./OfflineBill";
import { useAuth } from "../../../../../context/AuthContext";
import { SelectedProduct } from "./types";
import ClinicProductsSection from "./ClinicProductSection";
import { Product, StockItem } from "./types";
import { OrderService } from "../../../../../services/api/order.service";
import { ArrowLeft } from "lucide-react";
import { useBillGenerator } from "../../../../../hooks/useBillGenerator";

interface OfflineMainProps {
  handleBack: () => void;
  onOrderSuccess?: () => void;
}

export interface PatientFormData {
  patientName: string;
  patientMobile: string;
  patientAddress: string;
  patientPincode: string;
  paymentType: string;
  drName: string;
  mobileNumber: string;
  registrationNumber: string;
  clinicName: string;
  dlno: string;
  address: string;
  pincode: string;
  cashAmount: number;
}

const OfflineMain: React.FC<OfflineMainProps> = ({
  handleBack,
  onOrderSuccess,
}) => {
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    []
  );
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBill, setShowBill] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchText, setSearchText] = useState("");
  const { user } = useAuth();
  const [extraDiscount, setExtraDiscount] = useState(0);
  const [existingCoins, setExistingCoins] = useState(0);
  const clinicId = user?.role === "clinic" ? user?.id : "";
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItemsCount: 0,
    totalPages: 1,
    nextPage: null,
    previousPage: null,
  });
  const [coinsused, setCoinsused] = useState<number | "">("");
  const { generateBillForOrder, isGenerating, billComponent, error, reset } = useBillGenerator();
  const [formData, setFormData] = useState<PatientFormData>({
    patientName: "",
    patientMobile: "",
    patientAddress: "",
    patientPincode: "",
    paymentType: "",
    drName: "",
    mobileNumber: "",
    registrationNumber: "",
    clinicName: "",
    dlno: "",
    address: "",
    pincode: "",
    cashAmount: 0,
  });
  const [validationErrors, setValidationErrors] = useState({
    patientName: "",
    patientMobile: "",
    drName: "",
    patientPincode: "",
  });

  const validateForm = () => {
    console.log("Validating form data", formData);
    const newErrors = {
      patientName: formData?.patientName?.trim()
        ? ""
        : "Patient name is required",
      patientMobile: /^\d{10}$/.test(formData?.patientMobile)
        ? ""
        : "Valid mobile number is required",
      drName: formData?.drName?.trim() ? "" : "Doctor name is required",
      patientPincode:
        formData?.patientPincode?.length !== 6
          ? "Valid pincode is required"
          : "",
    };

    setValidationErrors(newErrors);
    console.log(
      Object.values(newErrors)
        .filter((error) => error !== "")
        .some((error) => error)
    );
    console.log(Object.values(newErrors));
    return Object.values(newErrors)
      .filter((error) => error !== "")
      .some((error) => error);
  };
  const fetchProducts = async (
    searchTerm = "",
    page = 1,
    itemsPerPage = 10
  ) => {
    try {
      setLoading(true);

      const response = await AdminOrderService.getClinicProductListing(
        page,
        itemsPerPage,
        searchTerm,
        clinicId
      );
      if (response?.data?.data && Array.isArray(response.data.data)) {
        const transformedProducts = response.data.data.map(
          (item: StockItem) => ({
            compositeId: `${item.id}_${item.product_id}`,
            id: item.id,
            product_id: item.product_id,
            product_name: item.product?.name || "Unknown Product",
            manufacturer_name:
              item.manufacturer?.manufacturer_name || "Unknown Manufacturer",
            product_image: item.product?.productImage || medicineIcon,
            landing_cost: parseFloat(item.landing_cost) || 0,
            mrp:
              parseFloat(item.productMrp || item.mrp),
            quantity: item.no_of_items || item.remaining_stocks || 0,
            expiry_date: item.expiry_date,
            batch_number: item.batch_number,
            hsn_number: item.hsn_number,
            product_rate: parseFloat(item.product_rate) || 0,
            discount: parseFloat(item.sellingDiscount) || 0,
            cgst: parseFloat(item.cgst) || 0,
            sgst: parseFloat(item.sgst) || 0,
            free_items: item.free_items || 0,
            finalSP:
              parseFloat(item.finalSP) /
              (item?.product?.tablet_per_stripe
                ? Math.max(1, item.product.tablet_per_stripe)
                : 1),
            sellingDiscount: 0,
            remainingStocks: item.remaining_stocks,
            totalStock: item.totalQuantity ? item.totalQuantity : (item.remaining_stocks * (item.product.tablet_per_stripe ? item.product.tablet_per_stripe : 1)),
            noOfItemsInPack: item?.product?.tablet_per_stripe
              ? Math.max(1, item.product.tablet_per_stripe)
              : 1,
            perPackPrice: parseFloat(item.productMrp || item.mrp) /
              (item?.product?.tablet_per_stripe
                ? Math.max(1, item.product.tablet_per_stripe)
                : 1) || 0,
            expiry: item.expiry_date,
            batch: item.batch_number,
            salt: item.salt,
          })
        );

        setAvailableProducts(transformedProducts);

        // Update pagination state
        setPagination(
          response.data.pagination || {
            currentPage: 1,
            itemsPerPage: 10,
            totalItemsCount: 0,
            totalPages: 1,
            nextPage: null,
            previousPage: null,
          }
        );
      } else {
        console.warn("Unexpected response format:", response);
        toast.error("Failed to load products - unexpected format");
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to load products"
      );
    } finally {
      setLoading(false);
    }
  };
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      fetchProducts(searchText, page, pagination.itemsPerPage);
    }
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    fetchProducts(searchText, 1, itemsPerPage);
  };

  useEffect(() => {
    fetchProducts(searchText, pagination.currentPage, pagination.itemsPerPage);
  }, [clinicId]);

  const handleSearch = (searchTerm: string) => {
    setSearchText(searchTerm);
    fetchProducts(searchTerm);
  };

  const handleAddProduct = (product: Product) => {
    // Check if product is already in selected products
    const existingProduct = selectedProducts.find(
      (p) => p.compositeId === product.compositeId
    );

    // Calculate requested quantity (1 if new, +1 if existing)
    const requestedQuantity = existingProduct
      ? existingProduct.selectedQuantity + 1
      : 1;

    // Check stock availability
    if (requestedQuantity > product.totalStock) {
      toast.error(
        `Stock unavailable! Only ${product.remainingStocks} items left`
      );
      return;
    }

    if (existingProduct) {
      setSelectedProducts((prev) =>
        prev.map((p) =>
          p.compositeId === product.compositeId
            ? { ...p, selectedQuantity: p.selectedQuantity + 1 }
            : p
        )
      );
    } else {
      setSelectedProducts((prev) => [
        ...prev,
        { ...product, selectedQuantity: 0 },
      ]);
    }
  };

  // Update quantity change logic
  const handleUpdateQuantity = (compositeId: string, quantity: number) => {
    // Find the product in available products to check stock
    const product = availableProducts.find(
      (p) => p.compositeId === compositeId
    );
    const selectedProduct = selectedProducts.find(
      (p) => p.compositeId === compositeId
    );

    if (!product || !selectedProduct) return;

    // Check stock availability
    if (quantity > product.totalStock) {
      toast.error(
        `Stock unavailable! Only ${product.remainingStocks} items left`
      );
      return;
    }

    // Update quantity if stock is available
    setSelectedProducts((prev) =>
      prev.map((p) =>
        p.compositeId === compositeId ? { ...p, selectedQuantity: quantity } : p
      )
    );
  };

  // Update remove product logic
  const handleRemoveProduct = (compositeId: string) => {
    setSelectedProducts((prev) =>
      prev.filter((p) => p.compositeId !== compositeId)
    );
  };

  const onUpdateDiscount = (productId: string, discount: number) => {
    const product = availableProducts.find((p) => p.compositeId === productId);
    const select = selectedProducts.find((p) => p.compositeId === productId);

    if (isNaN(discount)) {
      discount = 0;
    }

    if (!product || !select) return;

    if (discount > product.discount) {
      toast.error("Price less than selling price");
      setSelectedProducts((prev) =>
        prev.map((p) =>
          p.compositeId === productId ? { ...p, sellingDiscount: 0 } : p
        )
      );
      return;
    }

    setSelectedProducts((prev) =>
      prev.map((p) =>
        p.compositeId === productId ? { ...p, sellingDiscount: discount } : p
      )
    );
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // For mobile number, restrict to digits and max 10 characters
    if (name === "patientMobile") {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 10) return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const calculateTotals = () => {
    const totalItems = selectedProducts.reduce(
      (sum, product) => sum + product.selectedQuantity,
      0
    );

    const totalAmount = selectedProducts.reduce((sum, product) => {
      const mrp = product.perPackPrice;
      const qty = product.selectedQuantity;
      const discount = product.sellingDiscount ?? 0; // Use 0 if undefined/null

      const discountedPrice = mrp * (1 - discount / 100);
      return sum + discountedPrice * qty;
    }, 0);

    const decimalPart = totalAmount - Math.floor(totalAmount);
    let roundedAmount;

    if (decimalPart >= 0.5) {
      roundedAmount = Math.ceil(totalAmount - (coinsused || 0));
    } else {
      roundedAmount = Math.floor(totalAmount - (coinsused || 0));
    }
    const roundOffValue = parseFloat((roundedAmount - totalAmount).toFixed(2));

    return { totalItems, totalAmount, roundedAmount, roundOffValue };
  };

  const handleGenerateBill = async () => {
    const isDoctorRequired = await checkDoctorRequired(selectedProducts);
    if (isDoctorRequired) {
      if (validateForm()) {
        toast.error(
          "Patient and doctors details are required for added medicines"
        );
        return;
      }
    }
    if (selectedProducts.length === 0) {
      toast.error("Please add at least one product");
      return;
    }
    if (selectedProducts.some(p => !p.selectedQuantity || p.selectedQuantity === 0)) {
      toast.error("Products cannot have 0 quantity. Please update or remove them.");
      return;
    }
    if (!formData.paymentType) {
      toast.error("Payment type is required");
      return;
    }
    setShowBill(true);
  };

  const checkDoctorRequired = async (products: SelectedProduct[]) => {
    for (const product of products) {
      const check = await OrderService.checkProduct(product.product_id);
      if (check) {
        return true;
      }
    }
    return false;
  };

  const handlePrintAndProcess = async () => {
    const isDoctorRequired = await checkDoctorRequired(selectedProducts);
    if (isDoctorRequired) {
      if (validateForm()) {
        toast.error(
          "Patient and doctors details are required for added medicines"
        );
        return;
      }
    }

    if (selectedProducts.length === 0) {
      toast.error("Please add at least one product");
      return;
    }
    if (selectedProducts.some(p => !p.selectedQuantity || p.selectedQuantity === 0)) {
      toast.error("Products cannot have 0 quantity. Please update or remove them.");
      return;
    }

    setIsProcessing(true);
    try {
      const { totalItems, totalAmount, roundedAmount, roundOffValue } = calculateTotals();

      // 1. Calculate total MRP amount
      const totalMRPAmount = selectedProducts.reduce(
        (sum, product) => sum + product.mrp * product.selectedQuantity,
        0
      );

      // 2. Prepare products array with updated discount
      const updatedProducts = selectedProducts.map((product) => {
        const itemTotalMRP = product.mrp * product.selectedQuantity;
        const contributionRatio = itemTotalMRP / totalMRPAmount;
        const distributedDiscountRs = contributionRatio * extraDiscount;

        // Extra discount % based on per product's MRP total
        const extraDiscountPercent =
          (distributedDiscountRs / itemTotalMRP) * 100;

        const finalDiscountPercent =
          (product.sellingDiscount || 0) + extraDiscountPercent;

        return {
          productId: product.product_id,
          noOfItems: product.selectedQuantity,
          inwardStockId: product.id,
          mrp: product.mrp,
          productDiscount: parseFloat(finalDiscountPercent.toFixed(2)), // Round off to 2 decimal places
        };
      });

      let cashAmount = 0;
      if (formData.paymentType === "Cash") {
        cashAmount = roundedAmount - extraDiscount;
      } else if (formData.paymentType === "Split") {
        cashAmount = formData.cashAmount;
      }

      const coinsEarned = Math.floor((roundedAmount - extraDiscount - Number(coinsused || 0)) * 0.01);

      const payload = {
        clinicId: clinicId,
        patientName: formData.patientName ?? "-",
        patientMobileNumber: formData.patientMobile ?? "-",
        patientAddress: formData.patientAddress ?? "-",
        doctorName: formData.drName ?? "-",
        patientPincode: formData.patientPincode ?? "-",
        paymentType: formData.paymentType,
        cashAmount: cashAmount,
        totalAmount: roundedAmount - extraDiscount - Number(coinsused || 0) ,
        totalItems: totalItems,
        discount: extraDiscount ?? 0,
        products: updatedProducts,
        coinsEarned: coinsEarned,
        coinsUsed: coinsused,
      };

      console.log(payload);

      const response = await OfflineItemService.createOfflineOrder(payload);

      if (response.success) {
        toast.success("Order created successfully!");
        // Reset form after successful creation
        setShowBill(false);
        setSelectedProducts([]);
        setFormData({
          patientName: "",
          patientMobile: "",
          patientAddress: "",
          drName: "",
          mobileNumber: "",
          patientPincode: "",
          paymentType: "",
          registrationNumber: "",
          clinicName: "",
          dlno: "",
          address: "",
          pincode: "",
          cashAmount: 0,
        });
        handleBack()
      } else {
        throw new Error(response.message || "Failed to create order");
      }
    } catch (error) {
      console.error("Error creating offline order:", error);
      toast.error("Failed to create order");
    } finally {
      setIsProcessing(false);
    }
  };

  const { totalItems, totalAmount, roundOffValue, roundedAmount } = calculateTotals();

  const handleBackButton = () => {
    if (showBill) {
      setShowBill(false);
      setValidationErrors({
        patientName: "",
        patientMobile: "",
        drName: "",
        patientPincode: "",
      });

      setFormData((prev) => ({
        ...prev,
        patientName: "",
        patientMobile: "",
        patientAddress: "",
        drName: "",
        patientPincode: "",
        paymentType: "",
      }));
    } else {
      handleBack();
    }
  };

  const calculateTotalDiscount = () => {
    return selectedProducts.reduce((total, product) => {
      const discount = product.sellingDiscount || 0;
      return (
        total + ((product.perPackPrice * discount) / 100) * product.selectedQuantity
      );
    }, 0);
  };

  const totalPrice = () => {
    return selectedProducts.reduce((total, product) => {
      return total + product.perPackPrice * product.selectedQuantity;
    }, 0);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 w-full">
      
      <button
        onClick={handleBackButton}
        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors"
      >
        <ArrowLeft size={20} />
        {showBill ? "Back to Form" : "Back to Orders"}
      </button>

      <div className="w-full flex flex-col justify-start gap-2">
        {/* Main Content Area */}
        <div className="lg:col-span-1 w-full">
          <div
            className={`bg-white rounded-lg p-6 ${!showBill ? "flex justify-start items-center" : ""
              }`}
          >
            {showBill ? (
              <OfflineBill
                formData={formData}
                selectedProducts={selectedProducts}
                extraDiscount={extraDiscount}
                coinsUsed={coinsused}
              />
            ) : (
              <PatientDetailsForm
                formData={formData}
                handleInputChange={handleInputChange}
                setFormData={setFormData}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}

                setExistingCoins={setExistingCoins}
              />
            )}
          </div>
        </div>

        {/* Products Section - Only shown when not in bill view */}
        {!showBill && (
          <div className="w-full">
            <div className="bg-white rounded-lg h-full p-6">
              <ClinicProductsSection
                availableProducts={availableProducts}
                selectedProducts={selectedProducts}
                onAddProduct={handleAddProduct}
                onUpdateDiscount={onUpdateDiscount}
                onUpdateQuantity={handleUpdateQuantity}
                onRemoveProduct={handleRemoveProduct}
                loading={loading}
                error={error}
                onSearch={handleSearch}
                pagination={pagination}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </div>
        )}

        {/* Footer Section */}
        <div className="w-full p-6 flex justify-between">
          {!showBill && (
            <div className="w-1/3">
              <div>
                <label
                  htmlFor="paymentType"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Payment Type
                </label>
                <select
                  id="paymentType"
                  name="paymentType"
                  value={formData.paymentType}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select Payment Type</option>
                  <option value="Online">Online</option>
                  <option value="Cash">Cash</option>
                  <option value="Split">Split</option>
                </select>
              </div>
              {formData.paymentType === 'Split' && (
                <div className="mt-4">
                  <label
                    htmlFor="cashAmount"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Cash Amount
                  </label>
                  <input
                    type="number"
                    id="cashAmount"
                    name="cashAmount"
                    value={formData.cashAmount}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              )}
            </div>
          )}

          <ClinicOrderSummary
            totalItems={totalItems}
            totalAmount={totalAmount}
            onNotifyPatient={
              showBill ? handlePrintAndProcess : handleGenerateBill
            }
            actionText={showBill ? "Process Order" : "Generate Bill"}
            order={[]}
            setExtraDiscount={setExtraDiscount}
            totalDiscount={calculateTotalDiscount()}
            totalPrice={totalPrice()}
            isBillOpen={showBill}
            roundoffValue={roundOffValue}
            roundedAmount={roundedAmount}
            cashAmount={formData.cashAmount}
            paymentType={formData.paymentType}
            coins={existingCoins}
            coinsused={coinsused}
            isProcessing={isProcessing}
            setCoinsused={setCoinsused}
          />
        </div>
      </div>
    </div>
  );
};

export default OfflineMain;
